import { Button, buttonVariants } from "@/components/ui/button";
import { useIsThinkingDots } from "@/hooks";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import { cn } from "@/lib/utils";
import { Link, useMatches, useParams } from "@tanstack/react-router";
import { ChevronLeft, CircleCheck, Eye, Loader } from "lucide-react";
import { useState } from "react";
import type { FpFile } from "../../../../../schemas/codegen";

interface OtherAttachmentsProps {
  attachments: FpFile[];
  versionNumber: number;
}

export function OtherAttachments({
  attachments,
  versionNumber,
}: OtherAttachmentsProps) {
  const chatId = useActiveChatId();
  const versionNumberParam = useParams({
    strict: false,
    select: (params) => params.versionNumber,
  });

  // This shouldn't happen, but is here to satisfy the type checker
  if (!chatId) {
    throw new Error("No chat ID available");
  }

  const [isOpened, setIsOpened] = useState(true);

  const isViewingAnyFile = useMatches({
    select: (matches) => {
      const attachmentsMatch = matches.find(
        (match) =>
          match.routeId ===
          "/(codegen)/codegen/$chatId/attachments/$versionNumber",
      );

      if (!attachmentsMatch) {
        return false;
      }

      const search = attachmentsMatch.search as { tab?: string; file?: string };

      return attachments.some(
        (attachment) =>
          search?.file === attachment.filename &&
          search?.tab === "code" &&
          versionNumberParam === `v${versionNumber}`,
      );
    },
  });

  return (
    <details
      open={isOpened}
      onToggle={({ currentTarget }) => setIsOpened(currentTarget.open)}
    >
      <summary
        className={cn(
          "grid grid-cols-[auto_1fr_auto] justify-items-start items-center gap-3 list-none text-sm font-medium bg-input px-4 py-2",
          isOpened ? "rounded-ss-lg rounded-se-lg" : "rounded-lg",
        )}
      >
        <span className="text-sm font-mono">v{versionNumber}</span>

        <Link
          to="/codegen/$chatId/attachments/$versionNumber"
          params={{
            chatId,
            versionNumber: `v${versionNumber}`,
          }}
          search={{ tab: "code", file: attachments[0].filename }}
          className={cn(
            buttonVariants({ variant: "secondary", size: "sm" }),
            "border",
          )}
        >
          <Eye className="h-4 w-4" />
          {isViewingAnyFile ? "Viewing" : "View"}
        </Link>

        <Button size="icon-xs" onClick={() => setIsOpened((prev) => !prev)}>
          <ChevronLeft
            className={cn("h-full aspect-square transition-transform", {
              "rotate-[-90deg]": isOpened,
            })}
          />
        </Button>
      </summary>

      {/* NOTE: The bg-secondary/50 color is temporary until we know which colors to use */}
      <div className="flex flex-col gap-2 bg-secondary/50 px-4 py-2 rounded-es-lg rounded-ee-lg">
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className="grid grid-cols-[1rem_1fr_10ch] gap-3 place-items-start items-center"
          >
            {attachment.status === "processing" ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <CircleCheck className="w-4 h-4 animate-in" />
            )}
            <Link
              to="/codegen/$chatId/attachments/$versionNumber"
              params={{
                chatId,
                versionNumber: `v${versionNumber}`,
              }}
              search={{ tab: "code", file: attachment.filename }}
              className="font-mono text-sm"
            >
              {attachment.filename}
            </Link>

            <span className="text-sm font-mono">
              {attachment.status === "processing" && <GeneratingDots />}
            </span>
          </div>
        ))}
      </div>
    </details>
  );
}

function GeneratingDots() {
  const dots = useIsThinkingDots();

  return (
    <span className="text-sm text-muted-foreground">Generating{dots}</span>
  );
}
